// @ts-strict-ignore
/** @jsx jsx */
import { jsx } from 'theme-ui'
import React from 'react'
import customValidations from '@uswitch/ev-components/lib/components/helpers/form-validations'
import { SimpleInput } from '@uswitch/ev-components/lib/components/elements/simple-input'
import customFunctions from '@uswitch/ev-components/lib/components/helpers/form-validation-functions'
import { useComponentTracking } from '@uswitch/ev-utils/component-events'
import FormatStringToLocale from '@uswitch/ev-components/lib/components/helpers/number-formatter'
import { isValidNumber } from '@uswitch/ev-components/lib/components/helpers/field-input-validation'
import SimpleRadio from '@uswitch/ev-components/lib/components/elements/simple-radio'
import SimpleCheckbox from '@uswitch/ev-components/lib/components/elements/simple-checkbox'
import SimpleDropDown from '@uswitch/ev-components/lib/components/elements/simple-drop-down'
import InputAlert from '@uswitch/ev-components/lib/components/elements/input-alert'
import InputMask from 'react-input-mask'
interface CalculatorFieldProps {
  field: any
  register: (validation: any) => void
  errors: object
  getValues: any
  setValue: (name: string, value: string) => void
  variant: string
  trackInteraction: (m?: string, message?: string, error?: string) => void
}

const CalculatorField: React.FC<CalculatorFieldProps> = ({
  field,
  register,
  errors,
  getValues,
  setValue,
  variant = 'panel',
  trackInteraction = () => {},
}) => {
  const { validation: { custom, customFunction, ...rest } = {} } = field
  let {
    validation,
    name,
    type,
    placeholder,
    label,
    options = [],
    inputMask: { customMask, mask } = {},
    prefix,
    suffix,
    text,
    description,
    subtext,
    value = '',
    fields = [],
    icon,
  } = field

  const trackComponent = useComponentTracking()

  if (custom && customValidations[custom]) {
    validation = {
      ...rest,
      ...customValidations[custom],
    }
  }

  if (typeof customFunction === 'object') {
    const validateFunctions = {}
    let hasValidationFunctions = false
    for (const key in customFunction) {
      if (customFunctions[key]) {
        // For each custom function used in the data, generate the corresponding validation function

        validateFunctions[key] = customFunctions[key]({ ...customFunction[key], getValues })
        hasValidationFunctions = true
      }
    }
    if (hasValidationFunctions) {
      // See Validate option in https://react-hook-form.com/v5/api/#register

      validation.validate = validateFunctions
    }
  }

  const variantPath = (element: string) => `elements.form.variants.${variant}.${element}`

  const props = { name, placeholder, errors, setValue, options }

  const onChange = (e) => {
    const { value } = e.target

    if (customMask === 'currency') {
      const currencyString = FormatStringToLocale(value)
      return setValue(name, currencyString)
    }

    if (customMask === 'numeric') {
      const currentValue = isValidNumber(value) ? value : value.slice(0, -1)
      return setValue(name, currentValue)
    }

    setValue(name, value)
  }
  const ref = validation ? register(validation) : register

  const textField = (type === 'text' || type === 'redirect') && text

  const displayLabel = (label: string) => {
    return (
      <label
        sx={{
          variant: variantPath('label.variants.calculator'),
        }}
        htmlFor={label}
      >
        {label}
      </label>
    )
  }

  const displayDescription = (description: string) => {
    return (
      <p
        sx={{
          variant: variantPath('description'),
        }}
      >
        {description}
      </p>
    )
  }

  // TODO this only works for select dropdowns atm, could be extended for other use cases
  const swapInputs = (event, [field1, field2]) => {
    event.stopPropagation()

    const n1 = document.querySelector(`[name=${field1}]`)
    const n2 = document.querySelector(`[name=${field2}]`)

    const n1Children = Array.from(n1?.children)
    const n2Children = Array.from(n2?.children)
    const n1Selected = n1.selectedIndex
    const n2Selected = n2.selectedIndex

    n1?.replaceChildren(...n2Children)
    n2?.replaceChildren(...n1Children)

    n1.options[n2Selected].selected = true
    n2.options[n1Selected].selected = true

    const inputs = [n1, n2]
    inputs.forEach((d) => {
      d.focus()
      setTimeout(() => {
        d.blur()
      }, 0)
    })
  }

  if (errors[name]) {
    trackComponent('Form Event', 'error', { [name]: errors[name].message })
    trackInteraction(
      'GA Validation Error Triggered',
      'Site Interaction',
      `${name}:${errors[name].message}`,
    )
  }

  return (
    <React.Fragment>
      {type === 'dropdown' && (
        <div
          className='form-field-wrapper'
          sx={{
            variant: variantPath('input.calculator.dropdown'),
            alignSelf: 'flex-start',
          }}
        >
          {label && displayLabel(label)}
          {description && displayDescription(description)}
          <div
            sx={{
              variant: variantPath('wrapper.variants.calculator'),
            }}
          >
            <SimpleDropDown
              name={name}
              options={options}
              placeholder={placeholder}
              hasError={errors[name]}
              ref={ref}
              handleTrackInteraction={() => {}}
            />
          </div>
          {errors[name] && (
            <InputAlert type='alert' sx={{ variant: variantPath('input.error') }}>
              {errors[name].message}
            </InputAlert>
          )}
          {subtext && <p sx={{ textAlign: 'left' }}>{subtext}</p>}
        </div>
      )}
      {type === 'radio' && (
        <div
          className='form-field-wrapper'
          sx={{ variant: variantPath('input.radio.wrapper'), alignSelf: 'flex-start' }}
        >
          {label && displayLabel(label)}
          {description && displayDescription(description)}
          <div
            sx={{
              variant: variantPath('simple-radio.radioWrapper.variants.calculator'),
            }}
          >
            {options.map(({ label, value, defaultChecked = false, toolTip }, i) => (
              <div
                sx={{
                  variant: variantPath('simple-radio.span.variants.calculator'),
                }}
                key={i}
              >
                <SimpleRadio
                  key={i}
                  defaultChecked={defaultChecked}
                  label={label}
                  name={name}
                  value={value}
                  ref={ref}
                  toolTip={toolTip}
                />
              </div>
            ))}
          </div>
          {errors[name] && (
            <InputAlert
              type='alert'
              sx={{ variant: variantPath('input.error'), position: 'relative', top: 0 }}
            >
              {errors[name].message}
            </InputAlert>
          )}
        </div>
      )}
      {type === 'checkbox' && (
        <div
          className='form-field-wrapper'
          sx={{ variant: variantPath('input.checkbox.wrapper'), alignSelf: 'flex-start' }}
        >
          {label && displayLabel(label)}
          {description && displayDescription(description)}
          <div
            sx={{
              variant: variantPath('checkbox.wrapper.variants.calculator'),
            }}
          >
            {options.map(({ label, value, defaultChecked = false }, i) => (
              <div
                sx={{
                  variant: variantPath('checkbox.span.variants.calculator'),
                  input: {
                    appearance: 'auto',
                  },
                }}
                key={i}
              >
                <SimpleCheckbox
                  key={i}
                  defaultChecked={defaultChecked}
                  label={label}
                  value={value}
                  name={name}
                  ref={ref}
                />
              </div>
            ))}
          </div>

          {errors[name] && (
            <InputAlert type='alert' sx={{ variant: variantPath('input.error') }}>
              {errors[name].message}
            </InputAlert>
          )}
        </div>
      )}
      {(type === 'input' || type === 'tel') && (
        <div
          className='form-field-wrapper'
          sx={{
            variant: variantPath('input.wrapper.calculator'),
            display: 'block',
          }}
        >
          {label && (
            <label
              sx={{
                height: '48px',
                variant: variantPath('label.variants.calculator'),
              }}
              htmlFor={label}
            >
              {label}
            </label>
          )}
          {description && (
            <p
              sx={{
                variant: variantPath('description'),
              }}
            >
              {description}
            </p>
          )}
          <div
            sx={{
              variant: variantPath('wrapper.variants.calculator'),
            }}
          >
            {mask ? (
              <InputMask name={name} type={type} maskChar={0} onChange={onChange} mask={mask}>
                {(inputProps) => (
                  <SimpleInput
                    prefix={prefix}
                    suffix={suffix}
                    ref={ref}
                    {...inputProps}
                    hasError={errors[name]}
                    name={name}
                    placeholder={props.placeholder}
                    onChange={onChange}
                  />
                )}
              </InputMask>
            ) : (
              <SimpleInput
                prefix={prefix}
                suffix={suffix}
                ref={ref}
                hasError={errors[name]}
                onChange={onChange}
                name={name}
                type={type}
                placeholder={props.placeholder}
              />
            )}

            {subtext && <p sx={{ textAlign: 'left' }}>{subtext}</p>}
            {errors[name] ? (
              <InputAlert
                type='alert'
                sx={{ pl: '0', pr: '0', variant: variantPath('input.error') }}
              >
                {errors[name].message}
              </InputAlert>
            ) : (
              <div sx={{ '@media screen and (min-width: 768px)': { minHeight: '40px' } }}></div>
            )}
          </div>
        </div>
      )}

      {(type === 'text' || type === 'redirect') && (
        <div sx={{ variant: variantPath('input.wrapper'), alignSelf: 'flex-start' }}>
          <label sx={{ variant: variantPath('label') }}>{label}</label>
          <div
            sx={{
              pointerEvents: 'auto',
              variant: type === 'redirect' ? variantPath('redirect-text') : '',
            }}
            dangerouslySetInnerHTML={{ __html: textField }}
          ></div>
        </div>
      )}
      {type === 'hidden' && <input type='hidden' name={name} value={value} ref={ref} />}
      {type === 'swap' && (
        <div
          sx={{
            '@media screen and (max-width: 425px)': {
              padding: '8px 16px',
            },
            display: 'block',
            padding: '32px 16px',
          }}
        >
          <input
            id='swap'
            sx={{
              '@media screen and (max-width: 425px)': {
                transform: 'rotate(90deg)',
              },
              marginTop: '8px',
            }}
            alt={label}
            type='image'
            src={`/fs-core-assets/icons/money/${icon || 'swap-circle-plum'}.svg`}
            onClick={(e) => {
              e.preventDefault()
              swapInputs(e, fields)
            }}
          />
        </div>
      )}
    </React.Fragment>
  )
}

export default CalculatorField
